import React from 'react';

/**
 * StepIndicator Component
 *
 * Shows progress through a multi-step flow.
 * Used in onboarding and form wizards.
 */

export function StepIndicator({
  steps,
  currentStep,
  className = '',
}) {
  return (
    <div className={`step-indicator ${className}`} role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={steps}>
      {Array.from({ length: steps }).map((_, index) => {
        const isActive = index === currentStep;
        const isComplete = index < currentStep;

        return (
          <div
            key={index}
            className={`
              step-dot
              ${isActive ? 'step-dot-active' : ''}
              ${isComplete ? 'step-dot-complete' : ''}
            `.trim().replace(/\s+/g, ' ')}
            aria-label={`Step ${index + 1} of ${steps}${isActive ? ' (current)' : isComplete ? ' (complete)' : ''}`}
          />
        );
      })}
    </div>
  );
}

/**
 * NumberedStepIndicator - Shows step numbers
 */
export function NumberedStepIndicator({
  steps,
  currentStep,
  labels = [],
  className = '',
}) {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      {Array.from({ length: steps }).map((_, index) => {
        const isActive = index === currentStep;
        const isComplete = index < currentStep;
        const label = labels[index];

        return (
          <React.Fragment key={index}>
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  text-sm font-medium
                  transition-all duration-300
                  ${isActive ? 'bg-coral-500 text-white scale-110' : ''}
                  ${isComplete ? 'bg-coral-500 text-white' : ''}
                  ${!isActive && !isComplete ? 'bg-warm-200 text-warm-500' : ''}
                `}
              >
                {isComplete ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              {label && (
                <span
                  className={`
                    mt-2 text-xs
                    ${isActive ? 'text-coral-500 font-medium' : 'text-warm-500'}
                  `}
                >
                  {label}
                </span>
              )}
            </div>

            {/* Connector line */}
            {index < steps - 1 && (
              <div
                className={`
                  flex-1 h-0.5 mx-2
                  transition-colors duration-300
                  ${index < currentStep ? 'bg-coral-500' : 'bg-warm-200'}
                `}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * ProgressBar - Simple progress bar
 */
export function ProgressBar({
  value,
  max = 100,
  showLabel = false,
  className = '',
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={`w-full ${className}`}>
      <div className="h-2 bg-warm-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-coral-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <p className="mt-1 text-xs text-warm-500 text-right">
          {Math.round(percentage)}%
        </p>
      )}
    </div>
  );
}

export default StepIndicator;
